import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import ScmFormInputWrapper from "./ScmFormInputWrapper";
import { isSet } from "../../../Utils/CommonFunctions";
import ScmInputTextField from "./ScmInputTextField";
import { extractOptions, extractStylesAsObjects, extractFieldValue, getOutputFieldsElement } from "../MarkupFunctions";

const ScmFormField = ({ attributes, elmtParams }) => {
    const { convertAndRenderElement, children, defProps, dynamicVar: initialDynamicVar } = elmtParams;
    
    const rawFormName = attributes.name;
    let formName = rawFormName;
    const { field, entries, var: varName } = attributes;
    const dataSource = field || entries;
    
    const { onChangeResourcesFieldHandler, fieldFormPayload, rateWithShipments, outputResponse } = defProps;
    const [formData, setFormData] = useState([]);
    
    // Memoize the data extraction to prevent recalculation on every render
    const { iterateArrayObject, shouldLoop, updatedDynamicVar } = useMemo(() => {
        let iterateArrayObject = [];
        let shouldLoop = false;
        let updatedDynamicVar = {
            ...initialDynamicVar,
            ...outputResponse
        };
        
        if (dataSource) {
            try {
                // First try to get from rateWithShipments
                if (rateWithShipments && rateWithShipments[dataSource]) {
                    updatedDynamicVar = {
                        ...updatedDynamicVar,
                        [dataSource]: rateWithShipments[dataSource]
                    };
                }
                
                // Then try extractFieldValue
                try {
                    const extractedValue = extractFieldValue(updatedDynamicVar, dataSource);
                    if (extractedValue !== null && extractedValue !== undefined) {
                        updatedDynamicVar = {
                            ...updatedDynamicVar,
                            [dataSource]: extractedValue
                        };
                    }
                } catch(e) {
                    // Continue to next fallback
                }
                
                // Finally read from dynamicVar
                const extractedValue = updatedDynamicVar[dataSource];
                
                if (Array.isArray(extractedValue) && extractedValue.length > 0) {
                    iterateArrayObject = extractedValue;
                    shouldLoop = true;
                }
            } catch(e) {
                console.log('Error extracting field data:', e);
            }
        }
        
        return { iterateArrayObject, shouldLoop, updatedDynamicVar };
    }, [dataSource, initialDynamicVar, outputResponse, rateWithShipments]);
    
    // Initialize formData from extracted data if looping
    useEffect(() => {
        if (shouldLoop && iterateArrayObject.length > 0) {
            setFormData(prev => {
                if (prev.length !== iterateArrayObject.length) {
                    return iterateArrayObject.map((row, index) => {
                        return prev[index] || {};
                    });
                }
                return prev;
            });
        }
    }, [shouldLoop, iterateArrayObject.length]);
    
    const normalizeValue = (val) => {
        if (
            Array.isArray(val) &&
            val.length &&
            typeof val[0] === 'object' &&
            'value' in val[0]
        ) {
            return val[0].value;
        }
        return val;
    };
    
    const handleNestedInputChange = useCallback((fieldName, value, divIndex) => {
        const normalizedValue = normalizeValue(value);
    
        setFormData(prev => {
            const next = Array.isArray(prev) ? [...prev] : [];
            
            while (next.length <= divIndex) {
                next.push({});
            }
            
            next[divIndex] = {
                ...next[divIndex],
                [fieldName]: normalizedValue
            };
            
            return next;
        });
    }, []);
    
    // Use ref to track if we should send update (prevent infinite loop)
    const prevFormDataRef = useRef(JSON.stringify(formData));
    
    // When formData changes, call the parent's onChangeResourcesFieldHandler
    useEffect(() => {
        const currentFormDataStr = JSON.stringify(formData);
        
        // Only update if formData actually changed
        if (prevFormDataRef.current !== currentFormDataStr) {
            prevFormDataRef.current = currentFormDataStr;
            
            if (onChangeResourcesFieldHandler && formName) {
                const filteredData = formData.filter(item => 
                    item !== null && 
                    item !== undefined && 
                    Object.keys(item).length > 0
                );
                
                if (filteredData.length > 0) {
                    onChangeResourcesFieldHandler(
                        { api_name: formName, variable_name: formName },
                        filteredData
                    );
                }
            }
        }
    }, [formData, formName, onChangeResourcesFieldHandler]);
    
    // Render function - memoize to prevent recreation
    const renderChildrenInForm = useCallback((childrenArray, rowData = null, rowIndex = null) => {
        return childrenArray.map((child, divIndex) => {
            if (child.name === "scm:div") {
                const divChildren = isSet(child.children, []);
                const rawDivStyle = isSet(child.attribs?.style, '');
                const divStyle = typeof rawDivStyle === 'string' ? extractStylesAsObjects(rawDivStyle) : rawDivStyle;
                const divClassName = isSet(child.attribs?.class, '');
                
                const actualDivIndex = shouldLoop && rowIndex !== null 
                    ? rowIndex
                    : divIndex;
                
                return (
                    <div key={`${formName}_div_${actualDivIndex}`} data-scm-div-index={actualDivIndex} style={divStyle} className={divClassName}>
                        {divChildren.map((inputChild) => {
                            const normalizedTagName = isSet(inputChild?.name, '').toLowerCase();
                            if (!isSet(inputChild, null) || normalizedTagName === '' || !inputChild.attribs) {
                                return null;
                            }

                            if (normalizedTagName === "scm:inputtext") {
                                return (
                                    <ScmFormInputWrapper
                                        key={`${formName}_${isSet(inputChild?.attribs?.name, '')}_${rowIndex !== null ? rowIndex : actualDivIndex}`}
                                        inputChild={inputChild}
                                        formName={formName}
                                        divIndex={actualDivIndex}
                                        formData={formData}
                                        handleNestedInputChange={handleNestedInputChange}
                                        elmtParams={elmtParams}
                                        defProps={defProps}
                                    />
                                );
                            }
                            
                            const updatedElmtParams = rowData ? {
                                ...elmtParams,
                                dynamicVar: {
                                    ...updatedDynamicVar,
                                    [varName || 'row']: { ...rowData, loopIndex: rowIndex }
                                }
                            } : {
                                ...elmtParams,
                                dynamicVar: updatedDynamicVar
                            };
                            
                            return convertAndRenderElement([inputChild], { 
                                ...updatedElmtParams, 
                                formScope: formName, 
                                divName: `${formName}_div_${actualDivIndex}` 
                            });
                        })}
                    </div>
                );
            }
            return convertAndRenderElement([child], { 
                ...elmtParams, 
                dynamicVar: updatedDynamicVar,
                formScope: formName 
            });
        });
    }, [convertAndRenderElement, children, elmtParams, formName, formData, handleNestedInputChange, defProps, shouldLoop, varName, updatedDynamicVar]);
    
    // Main render logic
    if (shouldLoop && iterateArrayObject.length > 0) {
        return (
            <div {...attributes} data-scm-form-name={formName}>
                {iterateArrayObject.map((rowData, rowIndex) => {
                    return (
                        <div key={`${formName}_row_${rowIndex}`} data-scm-row-index={rowIndex}>
                            {renderChildrenInForm(children || [], rowData, rowIndex)}
                        </div>
                    );
                })}
            </div>
        );
    } else {
        return (
            <div {...attributes} data-scm-form-name={formName}>
                {renderChildrenInForm(children || [])}
            </div>
        );
    }
};
export default ScmFormField;

